import React, { useEffect } from 'react'

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import {
  AccessRestricted,
  ShowPageContainer,
} from '../../../components/PageLayout/ShowPage'
import { checkPath, useDeniedRoutes } from '../../../app/AppAuth/AppAuth.hooks'
import { ShowPageLoader } from '../../../components/Reusable/ContentLoaders/ShowPageLoader'
import { PageContainer } from '../../../components/PageLayout/PageLayout.styles'
import { useInAudit } from '../../../context/AuditContext'
import { showInfoToast } from '../../../Utils/helpers/toast'
import { ROOT_ID_PARAM_TYPE } from './RootPage.constants'

type RootPageProps = {
  idParamType?: ROOT_ID_PARAM_TYPE
  indexRoute: string
  indexPage: JSX.Element
  showRoute?: string
  showPage?: JSX.Element
  /** Additional routes spliced in just before the catch-all route, e.g. for
   * deep-linkable sub-pages that don't fit the single indexRoute/showRoute
   * shape this component otherwise supports. */
  extraRoutes?: JSX.Element[]
  onlyInAudit: boolean
  isGateKeeperEnabled: boolean
  /** When true, access checks are still in flight; do not redirect away yet. */
  isGateKeeperLoading?: boolean
  notAvailableString: string
  routeDisabled?: boolean
  transparent?: boolean
  fullWidth?: boolean
}

export const RootPage = ({
  idParamType,
  indexRoute,
  indexPage,
  showRoute,
  showPage,
  extraRoutes,
  onlyInAudit,
  isGateKeeperEnabled,
  isGateKeeperLoading = false,
  notAvailableString,
  routeDisabled = false,
  transparent = false,
  fullWidth = false,
}: RootPageProps): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const { auditId } = useInAudit()
  const params = useParams()
  const denyRoutes = useDeniedRoutes()

  const isFeatureDenied = checkPath(
    denyRoutes,
    location.pathname + location.search,
  )

  useEffect(() => {
    if (isGateKeeperLoading || isFeatureDenied) {
      return
    }
    if (!isGateKeeperEnabled || routeDisabled) {
      navigate(`/${params.pageContext}/dashboard`, { replace: true })
    }
    if (onlyInAudit && !auditId) {
      showInfoToast(notAvailableString)
      navigate(`/${params.pageContext}/dashboard`, { replace: true })
    }
  }, [
    auditId,
    navigate,
    params.pageContext,
    isGateKeeperEnabled,
    isGateKeeperLoading,
    isFeatureDenied,
    onlyInAudit,
    notAvailableString,
    routeDisabled,
  ])

  if (isGateKeeperLoading) {
    return (
      <PageContainer $transparent={transparent} $fullWidth={fullWidth}>
        <ShowPageLoader />
      </PageContainer>
    )
  }

  if (isFeatureDenied) {
    return (
      <PageContainer $transparent={transparent} $fullWidth={fullWidth}>
        <ShowPageContainer data-testid="show-page-access-restricted">
          <AccessRestricted />
        </ShowPageContainer>
      </PageContainer>
    )
  }

  if (onlyInAudit && !auditId) {
    return <></>
  }

  const showPath =
    showRoute && showPage
      ? idParamType
        ? `${showRoute}/:${idParamType}/*`
        : `${showRoute}/*`
      : undefined

  return (
    <PageContainer $transparent={transparent} $fullWidth={fullWidth}>
      <Routes>
        <Route path={indexRoute} element={indexPage} />
        {showPath && <Route path={showPath} element={showPage} />}
        {extraRoutes}
        <Route
          path="*"
          element={<Navigate replace to={`${indexRoute}?dismissed[]=false`} />}
        />
      </Routes>
    </PageContainer>
  )
}

RootPage.defaultProps = {
  onlyInAudit: false,
  notAvailableString: '',
  isGateKeeperEnabled: true,
}
